import React,{ useEffect,useRef,useState } from 'react';
import api from '../../config/api';

const endpoint='/api/cma-info/connection';
const authEndpoint='/api/cma-info/connection-auth';

// Opening or saving this form edits local encrypted configuration only. Vendor
// login verification has a separate explicit confirmation and never buys data.
export default function CmaInfoConnectionPanel({ contextKey,enabled=true,locked=false,onSaved,onBusyChange }) {
    const [connection,setConnection]=useState(null);
    const [accountOwnerName,setAccountOwnerName]=useState('');
    const [username,setUsername]=useState('');
    const [password,setPassword]=useState('');
    const [code,setCode]=useState('');
    const [awaitingCode,setAwaitingCode]=useState(false);
    const [clientLoginApproval,setClientLoginApproval]=useState(null);
    const [busy,setBusy]=useState(false);
    const [message,setMessage]=useState('Checking this CRM book’s client connection…');
    const generation=useRef(0);
    const active=useRef({contextKey,enabled});
    active.current={contextKey,enabled};
    const saveLock=useRef(false);
    const callback=useRef(onSaved);
    callback.current=onSaved;
    const busyCallback=useRef(onBusyChange);
    busyCallback.current=onBusyChange;
    const loginContext=JSON.stringify([contextKey,connection?.connectionId,connection?.revision]);
    const confirmClientAccount=clientLoginApproval===loginContext;

    useEffect(() => {
        busyCallback.current?.(busy);
        return () => {busyCallback.current?.(false);};
    },[busy]);

    useEffect(() => {
        const version=++generation.current;
        setConnection(null);setAccountOwnerName('');setUsername('');setPassword('');setCode('');setAwaitingCode(false);setClientLoginApproval(null);setBusy(false);
        saveLock.current=false;
        if (!enabled) return undefined;
        setMessage('Checking this CRM book’s client connection…');
        api.get(endpoint).then(({data}) => {
            if (generation.current!==version || active.current.contextKey!==contextKey || !active.current.enabled) return;
            setConnection(data);setAccountOwnerName(data?.accountOwnerName || '');
            setMessage(data?.message || 'The client connection’s status could not be confirmed.');
        }).catch(() => {
            if (generation.current===version && active.current.enabled) setMessage('Client account status could not be loaded. No provider sign-in or lookup was attempted.');
        });
        return () => { generation.current+=1; };
    },[contextKey,enabled]);

    const save=async event => {
        event.preventDefault();
        if (!enabled || locked || connection?.canManage!==true || saveLock.current) return;
        const version=generation.current;
        const body={accountOwnerName:accountOwnerName.trim(),username:username.trim(),password};
        if (!body.accountOwnerName || !body.username || !body.password.trim()) return;
        saveLock.current=true;setBusy(true);setUsername('');setPassword('');setCode('');setAwaitingCode(false);setClientLoginApproval(null);
        setMessage('Saving the client connection for verification…');
        const isCurrent=() => generation.current===version && active.current.contextKey===contextKey && active.current.enabled;
        try {
            const {data}=await api.put(endpoint,body);
            if (!isCurrent()) return;
            setConnection(data);setAccountOwnerName(data?.accountOwnerName || '');
            setMessage(data?.message || 'Saved for verification. Property-report access remains disabled until the client licence and billing are verified.');
            callback.current?.();
        } catch (error) {
            if (isCurrent()) setMessage(error.response?.data?.message || 'The client connection could not be saved. No provider sign-in or lookup was attempted.');
        } finally {
            if (isCurrent()) {saveLock.current=false;setBusy(false);}
        }
    };

    const verifyLogin=async action => {
        if (!enabled || locked || connection?.canManage!==true || !connection.configured || !connection.connectionId || !connection.revision || saveLock.current || !confirmClientAccount) return;
        if (action==='verify_device' && (!awaitingCode || !/^\d{6}$/.test(code))) return;
        const version=generation.current;
        const body={action,confirmClientAccount:true,connectionId:connection.connectionId,revision:connection.revision};
        if (action==='verify_device') body.code=code;
        saveLock.current=true;setBusy(true);setCode('');setUsername('');setPassword('');
        setMessage(action==='request_device' ? 'Requesting verification for the saved client login…' : 'Verifying the client login…');
        const isCurrent=() => generation.current===version && active.current.contextKey===contextKey && active.current.enabled;
        try {
            const {data}=await api.post(authEndpoint,body);
            if (!isCurrent()) return;
            if (!['verified','awaiting_code'].includes(data?.state) || !data.publicStatus) throw Error('The client login outcome could not be confirmed. Check its status before trying again.');
            setConnection(data.publicStatus);setAwaitingCode(data.state==='awaiting_code');
            setMessage(data.state==='awaiting_code'
                ? 'CMA Info requires a device verification code. Ask the registered client contact for that code, then enter it below. No report was requested.'
                : 'The client login was verified. Property-report licence and client billing approval are separate; this step does not enable lookups or buy a report.');
            callback.current?.();
        } catch (error) {
            if (isCurrent()) setMessage(error.response?.data?.message || error.message || 'The client login could not be verified. No report was requested.');
        } finally {
            if (isCurrent()) {saveLock.current=false;setBusy(false);}
        }
    };

    if (!enabled) return null;
    return <details style={{fontSize:12,marginTop:12}}>
        <summary>Client CMA Info connection</summary>
        <p role="status">Connection status: {message}</p>
        {connection?.accountOwnerName && <p>Client account: <strong>{connection.accountOwnerName}</strong></p>}
        {connection?.configured && <p>Client login verification: {connection.authenticationVerified ? 'Verified for the saved connection.' : 'Not yet verified for the saved connection.'}</p>}
        <p>Use the client’s own vendor account and credits for this CRM book. This connection supports property reports only. Contacts stay with Virtual Agent; WinDeed is separate. Identity, FICA and company or trust products are not enabled here.</p>
        {connection?.canManage===true ? <><form onSubmit={save} autoComplete="off" aria-label="Client CMA Info connection setup">
            <p>Enter the existing client-provided account. Saving stores its credentials securely for separate licence and billing verification. It does not sign in, enable CMA Info or buy a report.</p>
            <div style={{display:'grid',gap:8,maxWidth:400}}>
                <label>Client account name<input value={accountOwnerName} onChange={event => setAccountOwnerName(event.target.value)} maxLength={200} required disabled={busy || locked} autoComplete="off" /></label>
                <label>CMA Info username<input value={username} onChange={event => setUsername(event.target.value)} maxLength={320} required disabled={busy || locked} autoComplete="off" /></label>
                <label>CMA Info password<input type="password" value={password} onChange={event => setPassword(event.target.value)} maxLength={4096} required disabled={busy || locked} autoComplete="new-password" /></label>
                <button type="submit" disabled={busy || locked || !accountOwnerName.trim() || !username.trim() || !password.trim()}>Save for verification</button>
            </div>
        </form>
        {connection.configured && <section aria-label="Client CMA Info login verification" style={{marginTop:12}}>
            <p>Login verification contacts CMA Info for the saved client account. It does not approve the licence or authorise spending the client’s credits.</p>
            <label><input type="checkbox" checked={confirmClientAccount} disabled={busy || locked} onChange={event => setClientLoginApproval(event.target.checked ? loginContext : null)} /> I am authorised to verify this client’s own CMA Info login.</label>
            <div><button type="button" disabled={busy || locked || !confirmClientAccount || !connection.connectionId || !connection.revision} onClick={() => verifyLogin('request_device')}>Request login verification</button></div>
            {awaitingCode && <form aria-label="CMA Info device verification" autoComplete="off" onSubmit={event => {event.preventDefault();verifyLogin('verify_device');}}>
                <label>Client device verification code<input value={code} onChange={event => setCode(event.target.value.replace(/\D/g,'').slice(0,6))} inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="off" disabled={busy || locked} /></label>
                <button type="submit" disabled={busy || locked || !confirmClientAccount || !/^\d{6}$/.test(code)}>Verify client login</button>
            </form>}
        </section>}</> : connection && <p>Only the CRM book owner can save or replace its client connection.</p>}
    </details>;
}
